/**
 * Page d'accord parental — le PARENT clique le lien magique reçu par mail.
 *
 * URL : /auth/parental-consent?token=<uuid> — accessible sans compte.
 * Appelle l'edge fn publique `parental-consent-confirm` qui passe le
 * consentement de l'élève en `active`. Idempotente (re-clic → ok).
 */

import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CompassLogo,
} from '@jeraisonne/ui';
import { supabaseUrl, supabaseAnonKey } from '@/lib/supabase';

type State = { status: 'loading' } | { status: 'ok'; studentName: string } | { status: 'error' };

async function confirmConsent(token: string): Promise<{ student_name?: string }> {
  const res = await fetch(`${supabaseUrl}/functions/v1/parental-consent-confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new Error('confirm_failed');
  return (await res.json()) as { student_name?: string };
}

export function ParentalConsentPage() {
  const [state, setState] = useState<State>({ status: 'loading' });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setState({ status: 'error' });
      return;
    }
    confirmConsent(token)
      .then((res) => setState({ status: 'ok', studentName: res.student_name?.trim() ?? '' }))
      .catch(() => setState({ status: 'error' }));
  }, []);

  return (
    <div className="min-h-dvh bg-transparent">
      <header className="flex justify-center px-6 py-8">
        <CompassLogo size="lg" wordmarkClassName="text-ink-900" />
      </header>

      <main className="mx-auto flex w-full max-w-md flex-col px-6 pb-16 pt-4 sm:pt-12">
        <Card className="border-ink-100/60 shadow-lg">
          {state.status === 'loading' && (
            <CardHeader>
              <CardTitle>Validation en cours…</CardTitle>
              <CardDescription>Merci de patienter un instant.</CardDescription>
            </CardHeader>
          )}

          {state.status === 'ok' && (
            <>
              <CardHeader>
                <CardTitle>Autorisation enregistrée ✅</CardTitle>
                <CardDescription>
                  Merci !{' '}
                  {state.studentName ? <strong>{state.studentName}</strong> : 'Votre enfant'} peut
                  maintenant utiliser Jeraisonne.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert variant="info">
                  <AlertDescription>
                    Vous pouvez fermer cette page. Votre enfant a été prévenu par email qu'il peut
                    désormais se connecter.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </>
          )}

          {state.status === 'error' && (
            <>
              <CardHeader>
                <CardTitle>Lien invalide ou expiré</CardTitle>
                <CardDescription>
                  Ce lien d'autorisation n'est plus valable. Demandez à votre enfant de relancer la
                  demande depuis son compte, puis cliquez sur le nouveau lien reçu.
                </CardDescription>
              </CardHeader>
            </>
          )}
        </Card>
      </main>
    </div>
  );
}
